import { Capacitor } from '@capacitor/core';
import { saveUserItem, getUserItem } from '../data/userStore';
import { isInternalBuild } from '../config/distribution';
import { FULL_APP_KEY, FULL_APP_PRICE_LABEL } from '../data/entitlements';

// RevenueCat constate les achats ; le miroir user (scope 'purchase') reste le
// cache offline que toute la logique de gating lit deja. Ce module ne fait que
// reconcilier CustomerInfo -> miroir, jamais l'inverse, et seulement dans un
// sens : il POSE l'entitlement, il ne le retire jamais. Le retrait d'un accès
// appartient au serveur (voir modules/sync/repository.js côté API).
//
// Identite : configure() recoit le user_id Supabase (session anonyme posee au
// boot par ensureAnonSession). Sign-in et sign-out rechargent l'app
// (AccountSection), donc le boot suivant reconfigure RevenueCat avec la
// nouvelle identite : pas de logIn/logOut a cabler en cours de session.
//
// Deux backends derriere la meme API : le plugin Capacitor sur iOS/Android,
// @revenuecat/purchases-js sur web (checkout Stripe Billing / Managed
// Payments). Les deux sont charges dynamiquement : jamais evalues en jsdom,
// et webpack les sort du bundle principal.

// Cles API PUBLIQUES RevenueCat (publiables, une par plateforme).
// Web : cle de la config RevenueCat Billing (checkout adosse a Stripe). En dev
// local le mock reste actif ; une cle sandbox peut etre injectee via
// REACT_APP_RC_WEB_KEY.
const WEB_API_KEY_PROD = 'rcb_KGVxqKlMlTYwvpNvrriWOVaVpHwg';
const API_KEYS = {
  ios: 'appl_PlIajyZIecHmONeGZaeNJTYctlw',
  android: 'goog_igzlSxmSZABJCzJHcHmiKuqKeDA',
  web:
    process.env.REACT_APP_RC_WEB_KEY ||
    (process.env.NODE_ENV === 'production' ? WEB_API_KEY_PROD : ''),
};

let Purchases = null; // module natif, charge par configurePurchases
let cancelledCode = null; // PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR
let webPurchases = null; // instance purchases-js, chargee par configurePurchases
let webCancelledCode = null; // ErrorCode.UserCancelledError
let configured = false;
let cachedPriceLabel = null;

// Abonnement leger pour les hooks React : bump quand le prix localise arrive
// ou quand le flux reel devient actif (configure).
const listeners = new Set();
let version = 0;
function emit() {
  version += 1;
  for (const fn of listeners) fn();
}

export function subscribePurchases(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getPurchasesVersion() {
  return version;
}

// Remet l'etat module a zero. Reserve aux tests : en runtime la configuration
// n'est faite qu'une fois par lancement, et rien ne doit la defaire.
export function _resetPurchases() {
  Purchases = null;
  cancelledCode = null;
  webPurchases = null;
  webCancelledCode = null;
  configured = false;
  cachedPriceLabel = null;
  listeners.clear();
  version = 0;
}

// Vrai paiement actif : plateforme native avec une cle API declaree et
// configure() passe. Tant que c'est faux, l'app reste sur le mock beta.
export function hasRealPayments() {
  return configured;
}

// Prix localise de l'offering (ex: "30,00 €" selon le storefront), fallback
// sur le label code en dur tant que l'offering n'est pas charge.
export function getFullAppPriceLabel() {
  return cachedPriceLabel || FULL_APP_PRICE_LABEL;
}

// Couture de simulateur (spec v5, S16). En simulateur iOS, aucun achat n'est
// possible : le StoreKit d'un simulateur ne vend rien, et tant que l'IAP n'est
// pas cree (S15) il n'y a rien a vendre nulle part. RevenueCat se configurait
// quand meme, puisque la cle iOS est en dur, et realPayments passait a vrai :
// le mock qui permet de rejouer le parcours d'achat devenait inatteignable
// exactement la ou il est utile.
//
// Adossee a isInternalBuild() et non a une simple variable : un bundle de
// production est TOUJOURS public, quoi qu'on mette dans l'environnement, donc
// aucune CI distraite ne peut donner l'app par ce chemin. Meme regle que le
// mock lui-meme (config/distribution.js).
function paymentsDisabledForDev() {
  return isInternalBuild() && process.env.REACT_APP_DISABLE_RC === 'true';
}

function isSupportedPlatform() {
  if (paymentsDisabledForDev()) return false;
  return !!API_KEYS[Capacitor.getPlatform()];
}

// CustomerInfo -> miroir, dans ce sens uniquement. Pose l'entitlement quand
// RevenueCat le dit actif, et ne retire RIEN quand il revient vide, même une
// row qu'il avait posée lui-même. Un CustomerInfo vide n'est pas une preuve de
// non-achat : c'est peut-être un cache froid, un boot hors ligne, ou un achat
// transféré vers une autre identité par un Restore. Aucun de ces cas ne
// justifie de perdre l'accès localement, et le client n'est de toute façon plus
// propriétaire du retrait : seul le serveur retire un accès.
function reconcile(customerInfo) {
  const active = !!customerInfo?.entitlements?.active?.[FULL_APP_KEY];
  const existing = getUserItem('purchase', FULL_APP_KEY);
  if (active && !existing) {
    saveUserItem('purchase', FULL_APP_KEY, {
      purchased: true,
      source: 'revenuecat',
      at: new Date().toISOString(),
    });
  } else if (!active && existing?.source === 'revenuecat') {
    console.warn(
      '[purchases] CustomerInfo vide alors que le miroir porte un achat RevenueCat : miroir conservé, seul le serveur retire un accès.'
    );
  }
}

// Package lifetime de l'offering courant, quel que soit le backend.
async function getCurrentPackage() {
  const offerings = webPurchases
    ? await webPurchases.getOfferings()
    : await Purchases.getOfferings();
  return offerings.current?.lifetime || offerings.current?.availablePackages?.[0] || null;
}

async function refreshPriceLabel() {
  try {
    const pkg = await getCurrentPackage();
    const priceString = webPurchases
      ? pkg?.webBillingProduct?.currentPrice?.formattedPrice
      : pkg?.product?.priceString;
    if (priceString && priceString !== cachedPriceLabel) {
      cachedPriceLabel = priceString;
      emit();
    }
  } catch (e) {
    console.warn('[purchases] offering indisponible:', e.message);
  }
}

async function configureNative(appUserID) {
  const mod = await import('@revenuecat/purchases-capacitor');
  Purchases = mod.Purchases;
  cancelledCode = mod.PURCHASES_ERROR_CODE?.PURCHASE_CANCELLED_ERROR;
  if (process.env.NODE_ENV !== 'production') {
    await Purchases.setLogLevel({ level: mod.LOG_LEVEL.DEBUG });
  }
  await Purchases.configure({
    apiKey: API_KEYS[Capacitor.getPlatform()],
    appUserID,
  });
  configured = true;
  await Purchases.addCustomerInfoUpdateListener((info) => reconcile(info));
  emit();
  const { customerInfo } = await Purchases.getCustomerInfo();
  reconcile(customerInfo);
}

// Web : purchases-js n'a pas de listener CustomerInfo, la reconciliation se
// fait au boot et au retour de purchase()/getCustomerInfo().
async function configureWeb(appUserID) {
  const mod = await import('@revenuecat/purchases-js');
  webCancelledCode = mod.ErrorCode?.UserCancelledError;
  webPurchases = mod.Purchases.configure({
    apiKey: API_KEYS.web,
    appUserId: appUserID,
  });
  configured = true;
  emit();
  const customerInfo = await webPurchases.getCustomerInfo();
  reconcile(customerInfo);
}

// A appeler au boot apres ensureAnonSession. No-op sans cle pour la
// plateforme (le mock beta reste alors en place).
// Ne throw jamais : un echec laisse l'app sur le mock, sans casser le boot.
export async function configurePurchases(appUserID) {
  if (configured || !isSupportedPlatform() || !appUserID) return;
  try {
    if (Capacitor.isNativePlatform()) {
      await configureNative(appUserID);
    } else {
      await configureWeb(appUserID);
    }
    refreshPriceLabel();
  } catch (e) {
    console.error('[purchases] configuration RevenueCat echouee:', e.message);
  }
}

function isUserCancellation(e) {
  if (webPurchases) return webCancelledCode != null && e.errorCode === webCancelledCode;
  return e.userCancelled || (cancelledCode != null && e.code === cancelledCode);
}

// Achat full_app via l'offering courant (package lifetime). Sur web,
// purchase() presente le checkout (Stripe Billing) et resout au paiement.
// L'annulation par l'utilisateur n'est pas une erreur : { cancelled: true }.
export async function purchaseFullApp() {
  if (!configured) return { success: false, error: 'purchases_not_configured' };
  try {
    const pkg = await getCurrentPackage();
    if (!pkg) return { success: false, error: 'no_offering' };
    const { customerInfo } = webPurchases
      ? await webPurchases.purchase({ rcPackage: pkg })
      : await Purchases.purchasePackage({ aPackage: pkg });
    reconcile(customerInfo);
    return { success: !!customerInfo?.entitlements?.active?.[FULL_APP_KEY] };
  } catch (e) {
    if (isUserCancellation(e)) {
      return { success: false, cancelled: true };
    }
    console.error('[purchases] achat echoue:', e.message);
    return { success: false, error: e.message };
  }
}

// Sur web il n'y a pas de restore au sens store : les achats suivent
// l'app_user_id, un refresh de CustomerInfo suffit.
export async function restorePurchases() {
  if (!configured) return { success: false, error: 'purchases_not_configured' };
  try {
    const customerInfo = webPurchases
      ? await webPurchases.getCustomerInfo()
      : (await Purchases.restorePurchases()).customerInfo;
    reconcile(customerInfo);
    return { success: true, restored: !!customerInfo?.entitlements?.active?.[FULL_APP_KEY] };
  } catch (e) {
    console.error('[purchases] restore echoue:', e.message);
    return { success: false, error: e.message };
  }
}
